'use server'

import { apiRequest, ApiResponse } from '../request'

/**
 * Formas que a venda fecha sozinha — as do gateway, as mesmas do `PaymentOptions`.
 *
 * Dinheiro, maquininha, "outro" e o split entre formas NÃO estão aqui: para eles a venda vai
 * SEM `paymentMethod` ("decidir na tela de pagamento") e o operador é levado à cobrança.
 */
export type CounterSalePaymentMethod =
    | 'PIX'
    | 'CREDIT_CARD'
    | 'DEBIT_CARD'
    | 'BOLETO'

export interface CounterSaleLineInput {
    catalogId: number
    quantity: number
    /** Preço unitário negociado, em centavos. Tem precedência sobre o percentual. */
    unitPrice?: number
    discountPercent?: number
}

/** Uma linha como o SERVIDOR a resolveu. A tela exibe isto — nunca soma por conta. */
export interface CounterSaleLine {
    catalogId: number
    name: string
    quantity: number
    listPrice: number
    basePrice: number
    unitPrice: number
    lineTotal: number
    costPrice: number | null
    maxDiscount: number | null
    /** Menor preço unitário que o teto de desconto do item permite. */
    minUnitPrice: number
    stockEnabled: boolean
    stockQuantity: number | null
}

export interface CounterSaleQuote {
    lines: CounterSaleLine[]
    subtotal: number
    discount: number
    total: number
}

export interface CounterSaleResult {
    orderId: number
    billingId: number
    /**
     * A cobrança nasceu com forma definida?
     *
     * `false` = "decidir na tela de pagamento": o operador precisa ir à cobrança, onde
     * escolhe dinheiro, maquininha ou o split — e de onde sai o link.
     */
    paymentResolved: boolean
    quote: CounterSaleQuote
}

/** Um produto que se vende no balcão: sem variação, ou a própria variação. */
export interface CounterSaleProduct {
    id: number
    /** Nome completo: na variação, "Produto - rótulo". */
    name: string
    sku: string | null
    barcode: string | null
    photo: string | null
    /** Preço efetivo da filial (promocional quando vigente), em centavos. */
    price: number
    stockEnabled: boolean
    stockQuantity: number | null
}

/**
 * Busca do balcão por nome, rótulo da variação, SKU ou código de barras. O pai que tem
 * variações não vem: vêm as variações dele.
 */
/**
 * `catalogId`: um item só (botão "Vender" do catálogo). Produto com variações devolve as
 * variações, para o operador escolher.
 */
export async function searchCounterSaleProducts(
    search?: string,
    catalogId?: number
): Promise<ApiResponse<CounterSaleProduct[]>> {
    return apiRequest<CounterSaleProduct[]>('counter-sales/products', {
        params: {
            ...(search ? { search } : {}),
            ...(catalogId ? { catalogId } : {}),
        },
    })
}

/**
 * O resumo da venda, calculado no servidor.
 *
 * Chamado a cada mudança de linha, quantidade ou desconto: é a única fonte do total, do
 * piso de desconto e do saldo de cada item.
 */
export async function quoteCounterSale(
    lines: CounterSaleLineInput[]
): Promise<ApiResponse<CounterSaleQuote>> {
    return apiRequest<CounterSaleQuote>(
        'counter-sales/quote',
        { data: { lines } },
        'post'
    )
}

export async function createCounterSale(data: {
    clientId: number
    lines: CounterSaleLineInput[]
    /** Dia da venda (yyyy-MM-dd). Ausente = hoje. Vira o vencimento da cobrança. */
    saleDate?: string
    /** Ausente = decidir na tela da cobrança. */
    paymentMethod?: CounterSalePaymentMethod
    /** Parcelas no cartão combinadas com o cliente (trava o checkout). */
    installmentCount?: number
    description?: string
    notes?: string
}): Promise<ApiResponse<CounterSaleResult>> {
    return apiRequest<CounterSaleResult>('counter-sales', { data }, 'post')
}
